import { CaretDown } from '@app/components/icons/caret-down';
import { Tooltip } from '@app/components/tooltip';
import { CheckIcon } from '@app/features/analytics/icons/check';
import { Menu, Transition } from '@headlessui/react';
import cx from 'classnames';
import React, { Fragment } from 'react';

export interface SelectorOption {
  value: string;
  label: string;
}

export interface SelectorProps {
  options: SelectorOption[];
  Icon?: React.ReactNode;
  selectedValue: string;
  onChange: (value: string) => void;
  className?: string;
  dataTestId: string;
  disabled?: boolean;
  renderOption?: (option: SelectorOption) => React.ReactNode;
  renderSelected?: (option: SelectorOption) => React.ReactNode;
  label?: string;
  buttonClassName?: string;
  /** When true, uses Tooltip component for dropdown options instead of native title attribute */
  useTooltipForOptions?: boolean;
}

export const Selector: React.FC<SelectorProps> = ({
  options,
  Icon = null,
  selectedValue,
  onChange,
  className = '',
  dataTestId,
  disabled = false,
  renderOption,
  renderSelected,
  label,
  buttonClassName = 'h-9 px-4 gap-2',
  useTooltipForOptions = false,
}) => {
  const selectedOption =
    options.find(option => option.value === selectedValue) || options[0];

  const menuComponent = (
    <Menu
      as="div"
      className={cx('relative', label ? 'w-full min-w-0' : className)}
    >
      <Tooltip tooltip={selectedOption?.label || ''} placement="top-center">
        <Menu.Button
          aria-haspopup="listbox"
          aria-label={label || 'Select option'}
          data-testid={dataTestId}
          disabled={disabled}
          className={cx(
            `w-full ${buttonClassName} flex items-center border border-spring-neutral-b0/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`,
            {
              'hover:border-spring-neutral-b0/40': !disabled,
              'opacity-50 cursor-not-allowed': disabled,
            },
          )}
        >
          {Icon && <div className="flex-shrink-0">{Icon}</div>}
          <div className="flex-1 text-left overflow-hidden">
            {renderSelected && selectedOption ? (
              renderSelected(selectedOption)
            ) : (
              <span className="text-sm text-white font-normal truncate block">
                {selectedOption?.label}
              </span>
            )}
          </div>
          <div className="flex-shrink-0 pl-0">
            <CaretDown className="w-4 h-4 text-white ui-open:rotate-180 transition-transform duration-200" />
          </div>
        </Menu.Button>
      </Tooltip>

      <Transition
        as={Fragment}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Menu.Items className="absolute z-10 mt-1 w-full bg-black border border-white/20 rounded-lg shadow-lg focus:outline-none">
          <div className="py-1">
            {options.map(option => {
              const isSelected = selectedValue === option.value;
              const optionButton = (active: boolean) => (
                <button
                  {...(!useTooltipForOptions && { title: option.label })}
                  onClick={() => onChange(option.value)}
                  className={cx(
                    'group flex w-full items-center justify-between px-3 py-2 text-sm text-spring-neutral-b0',
                    {
                      'bg-white/10': active,
                      'bg-transparent': !active,
                    },
                  )}
                  data-testid={`option-${option.value}`}
                >
                  <>
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <span className="truncate flex-1 text-left w-fit">
                        {option.label}
                      </span>
                    )}
                    {isSelected && <CheckIcon className="flex-shrink-0 ml-2" />}
                  </>
                </button>
              );

              return (
                <Menu.Item key={option.value}>
                  {({ active }) =>
                    useTooltipForOptions ? (
                      <Tooltip tooltip={option.label} placement="left-center">
                        {optionButton(active)}
                      </Tooltip>
                    ) : (
                      optionButton(active)
                    )
                  }
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );

  // If label is provided, wrap in flex-col container
  if (label) {
    return (
      <div className={cx('flex flex-col gap-2 min-w-0', className)}>
        <label
          htmlFor={dataTestId}
          className="text-xs font-medium leading-[1.416] text-spring-neutral-b4-muted m-0"
        >
          {label}
        </label>
        {menuComponent}
      </div>
    );
  }

  return menuComponent;
};
